import React, { useState, useEffect } from "react";
import { Card, CardHeader, Typography } from "@material-tailwind/react";
import LatestNews from "../components/Latest_news/latest_news";
import Footer from "../components/footer";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import StatsCounter from "../components/statistics";
import { API_URL } from "../App.js";
import { Link } from "react-router-dom";
import { GiDna2, GiProcessor, GiMicroscope, GiArtificialIntelligence, GiAtom } from "react-icons/gi";

// Imagen por defecto si no hay imágenes en el carrusel
const defaultImage = process.env.PUBLIC_URL + "/Foto_AdaLab.jpg";


// Componentes para flechas del carrusel
const NextArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="absolute right-6 top-1/2 -translate-y-1/2 z-50 bg-white/30 hover:bg-white/50 backdrop-blur-sm rounded-full p-3 transition-all duration-300 shadow-lg"
      style={{ width: '50px', height: '50px' }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={3}
        stroke="white"
        className="w-full h-full"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
      </svg>
    </button>
  );
};

const PrevArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="absolute left-6 top-1/2 -translate-y-1/2 z-50 bg-white/30 hover:bg-white/50 backdrop-blur-sm rounded-full p-3 transition-all duration-300 shadow-lg"
      style={{ width: '50px', height: '50px' }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={3}
        stroke="white"
        className="w-full h-full"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
      </svg>
    </button>
  );
};

const Home = () => {
  const [temas, setTemas] = useState([]);
  const [carruselImages, setCarruselImages] = useState([]);
  const [imagenObjetivos, setImagenObjetivos] = useState(null);

  // Iconos y colores de respaldo si no hay imagen
  const iconos = [GiDna2, GiProcessor, GiMicroscope, GiArtificialIntelligence, GiAtom];
  const colores = ["from-blue-500 to-cyan-400", "from-red-500 to-orange-400", "from-teal-500 to-green-400", "from-purple-500 to-pink-400", "from-amber-500 to-yellow-400"];



  useEffect(() => {
    // Obtener temas de investigación
    fetch(API_URL + "temas-investigacion/")
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          setTemas(data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching temas:", error);
      });

    // Obtener imágenes del carrusel con configuración individual
    fetch(API_URL + "carrusel-imagenes/")
      .then((response) => response.json())
      .then((data) => {
        console.log("Carrusel data:", data);
        if (data.message === "success" && data.data.length > 0) {
          setCarruselImages(data.data);
        } else {
          setCarruselImages([{
            id: 0,
            imagen: defaultImage,
            color_texto: "#FFFFFF",
            duracion: 5000,
            sombra_texto: true
          }]);
        }
      })
      .catch((error) => {
        console.error("Error fetching carrusel:", error);
        setCarruselImages([{
          id: 0,
          imagen: defaultImage,
          titulo: "Bienvenido a AdaLab",
          color_texto: "#FFFFFF",
          duracion: 5000,
          sombra_texto: true
        }]);
      });

    // Obtener imagen de objetivos
    fetch(API_URL + "imagen-objetivos/")
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success" && data.data.imagen) {
          setImagenObjetivos(data.data.imagen);
        }
      })
      .catch((error) => {
        console.error("Error fetching imagen objetivos:", error);
      });
  }, []);

  // Configuración del carrusel - ajusta según número de imágenes
  const hasMultipleImages = carruselImages.length > 1;

  // Calcular duración promedio para autoplay (si las imágenes tienen diferentes duraciones)
  const averageDuration = carruselImages.length > 0
    ? carruselImages.reduce((sum, img) => sum + (img.duracion || 5000), 0) / carruselImages.length
    : 5000;

  const settings = {
    dots: hasMultipleImages,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: hasMultipleImages,
    autoplaySpeed: averageDuration,
    cssEase: "ease-in-out",
    fade: true,
    pauseOnHover: false,
    arrows: hasMultipleImages,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (current, next) => {
      // Ajustar velocidad según duración de la siguiente imagen
      if (carruselImages[next] && carruselImages[next].duracion) {
        settings.autoplaySpeed = carruselImages[next].duracion;
      }
    }
  };

  // No renderizar el slider hasta que tengamos imágenes
  if (carruselImages.length === 0) {
    return (
      <div className="relative flex content-center items-center justify-center mt-3 bg-gray-200" style={{ aspectRatio: '16/5' }}>
        <div className="animate-pulse text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full overflow-hidden mt-3" style={{ aspectRatio: '16/5' }}>
        <Slider {...settings}>
          {carruselImages.map((item, index) => {
            // Determinar la URL de la imagen
            let imageUrl = item.imagen;
            if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
              imageUrl = '/' + imageUrl;
            }

            // Estilos de sombra de texto
            const textShadowStyle = item.sombra_texto
              ? '2px 2px 8px rgba(0,0,0,0.8), -1px -1px 4px rgba(0,0,0,0.6)'
              : 'none';

            return (
              <div key={item.id || index}>
                <div
                  className="relative w-full"
                  style={{ aspectRatio: '16/5' }}
                >
                  <img
                    src={imageUrl}
                    alt={item.titulo || 'Carrusel'}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-b from-black/20 via-transparent to-black/50" />

                  {/* Degradé inferior (30%) detrás del título */}
                  <div
                    className="absolute left-0 bottom-0 w-full pointer-events-none"
                    style={{
                      height: '30%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0) 100%)',
                      zIndex: 1
                    }}
                  />

                  {/* Franja con título de la imagen - horizontal completa */}
                  {item.titulo && (
                    <>
                      <div className="absolute left-0 right-0 py-2" style={{ bottom: '60px', zIndex: 2 }}>
                        <Typography
                          variant="h1"
                          className="font-bold font-sans text-center px-4 text-base sm:text-xl md:text-2xl lg:text-4xl"
                          style={{
                            color: item.color_texto || '#FFFFFF',
                            textShadow: textShadowStyle
                          }}
                        >
                          {item.titulo}
                        </Typography>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
      <section className="-mt-32 bg-gray-50 px-4 pb-2 pt-4 font-sans">
        <div className="container mx-auto font-sans">
          <div className={`flex flex-wrap justify-center gap-6 py-5 font-sans`}>
            {temas.map((tema, index) => {
              const IconComponent = iconos[index % iconos.length];
              const gradiente = colores[index % colores.length];
              const tieneIcono = tema.icono && tema.icono !== null;

              return (
                <Link to={`/projects/${tema.id}`} key={tema.id} className="no-underline flex-shrink-0">
                  <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden hover:-translate-y-2 w-[350px] h-[120px]">
                    <div className="relative px-6 h-full flex items-center gap-4">
                      <div className={`w-16 h-16 flex-shrink-0 rounded-full ${!tieneIcono ? `bg-gradient-to-br ${gradiente}` : 'bg-white'} flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300 overflow-hidden`}>
                        {tieneIcono ? (
                          <img
                            src={tema.icono}
                            alt={tema.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IconComponent className="w-8 h-8 text-white" />
                        )}
                      </div>
                      <h3 className="text-2xl font-bold font-sans text-gray-800 group-hover:text-colorPrincipal transition-colors duration-300 leading-tight">
                        {tema.nombre}
                      </h3>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-6 mb-2">
            <Typography
              variant="h2"
              className="mb-4 font-extrabold font-sans tracking-tight text-gray-900 text-center"
            >
              Objetivos
            </Typography>
            <div className="w-20 h-1 bg-gradient-to-r from-colorPrincipal to-colorSecudario rounded-full mx-auto"></div>
          </div>

          <div className="mb-6 flex flex-wrap items-center gap-8">
            <div className="w-full px-4 lg:w-[58%] order-1">
              <div className="space-y-5">
                <div className="flex gap-4 group">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                      1
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-sans text-gray-700 leading-relaxed text-justify">
                      Adquirir conocimiento permanente del estado del arte de las
                      ciencias de la computación, computación inteligente y
                      analítica computacional a nivel nacional e internacional para
                      su apropiación en la región como soporte tecnológico en los
                      diferentes sectores académicos, de investigación o industria.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                      2
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-sans text-gray-700 leading-relaxed text-justify">
                      Apropiar las tecnologías de ciencias de la computación,
                      computación inteligente y analítica computacional para el
                      almacenamiento, gestión, procesamiento y/o análisis de los
                      datos con modelado y soluciones computacionales en diferentes
                      áreas de aplicación.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                      3
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-sans text-gray-700 leading-relaxed text-justify">
                      Desarrollar habilidades y capacidades para soportar soluciones
                      con computación de alto desempeño que permitan el uso,
                      procesamiento o análisis eficiente o masivo de la información.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full flex justify-center px-4 lg:w-[38%] order-2">
              <div className="relative group w-full max-w-md">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-2xl blur-xl opacity-10 group-hover:opacity-15 transition duration-500"></div>
                <Card className="relative shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden rounded-2xl border border-gray-200">
                  <CardHeader className="relative h-72 m-0 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent z-10"></div>
                    <img
                      alt="AdaLab"
                      src={imagenObjetivos || (process.env.PUBLIC_URL + "/Logo_AdaLab.png")}
                      className="h-full w-full object-contain p-0 transform group-hover:scale-110 transition-transform duration-700"
                    />
                  </CardHeader>

                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StatsCounter />
      <div
        style={{
          height: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h3"
          className="font-bold font-sans tracking-wide"
          color="blue-gray"
        >
          Noticias recientes
        </Typography>
      </div>
      <LatestNews noticiasPerPages={5} />
      <Footer />
    </>
  );
};
export default Home;
